import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { projectApi, statsApi, templateApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Plus, 
  MessageSquare, 
  CheckCircle2, 
  XCircle, 
  Clock,
  TrendingUp,
  Loader2,
  Send,
  DollarSign,
  Hash,
  AlertCircle,
  Key,
  Paperclip,
  X,
  Bookmark,
  Trash2,
  FileText,
  Image,
  File,
  User,
  Bot,
  Sparkles,
  Search,
  Users,
  Target,
  LayoutList
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';

const StatusBadge = ({ status, phase, language }) => {
  const config = {
    analyzing: { color: 'bg-info/20 text-info', icon: Loader2, animate: true, label: { it: 'Analisi', en: 'Analyzing' } },
    questions: { color: 'bg-warning/20 text-warning', icon: MessageSquare, label: { it: 'Domande', en: 'Questions' } },
    clarification: { color: 'bg-warning/20 text-warning', icon: MessageSquare, label: { it: 'Domande', en: 'Questions' } },
    configuration: { color: 'bg-primary/20 text-primary', icon: Clock, label: { it: 'Configurazione', en: 'Configuration' } },
    ready: { color: 'bg-primary/20 text-primary', icon: CheckCircle2, label: { it: 'Pronto', en: 'Ready' } },
    executing: { color: 'bg-warning/20 text-warning', icon: Loader2, animate: true, label: { it: 'Esecuzione', en: 'Executing' } },
    recalculating: { color: 'bg-warning/20 text-warning', icon: Loader2, animate: true, label: { it: 'Ricalcolo', en: 'Recalculating' } },
    completed: { color: 'bg-success/20 text-success', icon: CheckCircle2, label: { it: 'Completato', en: 'Completed' } },
    error: { color: 'bg-destructive/20 text-destructive', icon: XCircle, label: { it: 'Errore', en: 'Error' } },
  };
  
  const key = phase || status;
  const { color, icon: Icon, animate, label } = config[key] || config.analyzing;
  const displayLabel = label ? label[language || 'it'] : key;
  
  return (
    <Badge className={`${color} gap-1 font-code text-xs uppercase tracking-wider`}>
      <Icon className={`w-3 h-3 ${animate ? 'animate-spin' : ''}`} />
      {displayLabel}
    </Badge>
  );
};

// File type icons
const getFileIcon = (type) => {
  if (type?.startsWith('image/')) return Image;
  if (type?.includes('pdf') || type?.includes('document')) return FileText;
  return File;
};

// Max file size: 10MB
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_FILES = 5;
const ALLOWED_TYPES = [
  'application/pdf',
  'text/plain',
  'text/csv',
  'text/markdown',
  'text/md',
  'application/json',
  'text/xml',
  'application/xml',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/png',
  'image/jpeg',
  'image/webp'
];

export default function DashboardPage() {
  const { t, language, hasOpenRouterKey } = useAuth();
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();
  
  // Chat state for new project creation
  const [chatMessages, setChatMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [readyToCreate, setReadyToCreate] = useState(false);
  const [suggestedExperts, setSuggestedExperts] = useState([]);
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);
  
  // Analysis types checkboxes
  const [analysisTypes, setAnalysisTypes] = useState({
    technical: false,
    feasibility: false,
    marketing: false,
    sales: false,
    financial: false,
    legal: false,
    operational: false,
    strategic: false,
    cost_comparison: false
  });
  
  // Analysis mode (specialists vs comparison)
  const [analysisMode, setAnalysisMode] = useState('specialists');
  
  // Templates
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showCreateTemplateDialog, setShowCreateTemplateDialog] = useState(false);
  
  // Search state
  const [projectSearch, setProjectSearch] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');
  
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    team_size: 'normal',
    accuracy_level: 'base',
    experts: []
  });
  const [newExpert, setNewExpert] = useState({ role: '', llm_model: 'anthropic/claude-3.5-sonnet' });
  
  const analysisTypeLabels = {
    technical: { it: 'Analisi Tecnica', en: 'Technical Analysis' },
    feasibility: { it: 'Fattibilità', en: 'Feasibility' },
    marketing: { it: 'Marketing', en: 'Marketing' },
    sales: { it: 'Vendite', en: 'Sales' },
    financial: { it: 'Finanziaria', en: 'Financial' },
    legal: { it: 'Legale/Normativa', en: 'Legal/Regulatory' },
    operational: { it: 'Operativa', en: 'Operational' },
    strategic: { it: 'Strategica', en: 'Strategic' },
    cost_comparison: { it: 'Confronto Costi AI vs Tradizionale', en: 'AI vs Traditional Cost Comparison' }
  };

  useEffect(() => {
    loadData();
  }, []);
  
  // Scroll to bottom when chat updates
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const loadData = async () => {
    try {
      const [projectsRes, statsRes, templatesRes] = await Promise.all([
        projectApi.list({ limit: 10 }),
        statsApi.get(),
        templateApi.list().catch(() => ({ data: [] })),
      ]);
      setProjects(projectsRes.data);
      setStats(statsRes.data);
      setTemplates(templatesRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteTemplate = async (templateId, e) => {
    e.stopPropagation();
    try {
      await templateApi.delete(templateId);
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success(language === 'it' ? 'Template eliminato' : 'Template deleted');
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nell\'eliminazione' : 'Failed to delete');
    }
  };
  
  // Create new template
  const handleCreateTemplate = async () => {
    if (!newTemplate.name.trim()) {
      toast.error(language === 'it' ? 'Inserisci un nome per il template' : 'Enter a template name');
      return;
    }
    try {
      const response = await templateApi.create(newTemplate);
      setTemplates(prev => [...prev, response.data]);
      setShowCreateTemplateDialog(false);
      setNewTemplate({ name: '', description: '', team_size: 'normal', accuracy_level: 'base', experts: [] });
      toast.success(language === 'it' ? 'Template creato!' : 'Template created!');
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nella creazione' : 'Creation failed');
    }
  };

  const addExpertToTemplate = () => {
    if (!newExpert.role.trim()) return;
    setNewTemplate(prev => ({
      ...prev,
      experts: [...prev.experts, { ...newExpert }]
    }));
    setNewExpert({ role: '', llm_model: 'anthropic/claude-3.5-sonnet' });
  };

  const removeExpertFromTemplate = (index) => {
    setNewTemplate(prev => ({
      ...prev,
      experts: prev.experts.filter((_, i) => i !== index)
    }));
  };
  
  // Reset chat state when dialog closes
  const handleDialogChange = (open) => {
    setDialogOpen(open);
    if (!open) {
      setChatMessages([]);
      setCurrentMessage('');
      setAttachments([]);
      setAnalyzing(false);
      setReadyToCreate(false);
      setSuggestedExperts([]);
      setAnalysisTypes({
        technical: false,
        feasibility: false,
        marketing: false,
        sales: false,
        financial: false,
        legal: false,
        operational: false,
        strategic: false,
        cost_comparison: false
      });
      setAnalysisMode('specialists');
    }
  };
  
  // Handle file upload
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    
    for (const file of files) {
      if (attachments.length >= MAX_FILES) {
        toast.error(language === 'it' ? `Massimo ${MAX_FILES} file` : `Maximum ${MAX_FILES} files`);
        break;
      }
      
      if (file.size > MAX_FILE_SIZE) {
        toast.error(language === 'it' ? 'File troppo grande (max 10MB)' : 'File too large (max 10MB)');
        continue;
      }
      
      if (file.size === 0) {
        toast.error(language === 'it' ? 'File vuoto' : 'Empty file');
        continue;
      }
      
      // Read file content
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result?.split(',')[1] || '';
        // Use a fallback content_type for markdown files
        let contentType = file.type;
        if (!contentType || contentType === '') {
          // Detect by extension
          const ext = file.name.split('.').pop()?.toLowerCase();
          if (ext === 'md') contentType = 'text/markdown';
          else if (ext === 'json') contentType = 'application/json';
          else if (ext === 'xml') contentType = 'application/xml';
          else contentType = 'text/plain';
        }
        setAttachments(prev => [...prev, {
          filename: file.name,
          content_type: contentType,
          content_base64: base64,
          size: file.size
        }]);
        toast.success(language === 'it' ? `File "${file.name}" allegato` : `File "${file.name}" attached`);
      };
      reader.onerror = () => {
        toast.error(language === 'it' ? `Errore lettura file "${file.name}"` : `Error reading file "${file.name}"`);
      };
      reader.readAsDataURL(file);
    }
    
    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  
  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };
  
  // Send message to pre-analyzer
  const handleSendMessage = async () => {
    if (!currentMessage.trim() && attachments.length === 0) return;
    
    if (!hasOpenRouterKey) {
      toast.error(language === 'it' ? 'Configura prima la API Key OpenRouter' : 'Configure OpenRouter API Key first');
      navigate('/settings');
      return;
    }
    
    // Build enhanced prompt with analysis types
    const selectedTypes = Object.entries(analysisTypes)
      .filter(([_, selected]) => selected)
      .map(([type, _]) => analysisTypeLabels[type][language] || analysisTypeLabels[type].en);
    
    let enhancedPrompt = currentMessage;
    if (selectedTypes.length > 0) {
      const typesText = language === 'it' 
        ? `\n\n[Tipi di analisi richieste: ${selectedTypes.join(', ')}]`
        : `\n\n[Required analysis types: ${selectedTypes.join(', ')}]`;
      enhancedPrompt += typesText;
    }
    
    const userMessage = { role: 'user', content: currentMessage };
    setChatMessages(prev => [...prev, userMessage]);
    setCurrentMessage('');
    setAnalyzing(true);
    
    try {
      const response = await projectApi.preAnalyze({
        prompt: enhancedPrompt,
        language,
        chat_history: chatMessages,
        attachments: attachments.map(a => ({
          filename: a.filename,
          content_type: a.content_type,
          content_base64: a.content_base64  // Include full content!
        })),
        analysis_types: selectedTypes,
        analysis_mode: analysisMode  // Pass the analysis mode!
      });
      
      const { response: analyzerResponse } = response.data;
      
      // Add analyzer response to chat
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: analyzerResponse.message,
        questions: analyzerResponse.questions || [],
        ready: analyzerResponse.ready_to_proceed
      }]);
      
      if (analyzerResponse.ready_to_proceed) {
        setReadyToCreate(true);
        setSuggestedExperts(analyzerResponse.suggested_experts || []);
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Pre-analysis failed');
      // Remove the user message if analysis failed
      setChatMessages(prev => prev.slice(0, -1));
    } finally {
      setAnalyzing(false);
    }
  };
  
  // Create project with full chat history
  const handleCreateProject = async () => {
    if (chatMessages.length === 0) {
      toast.error(language === 'it' ? 'Avvia prima una conversazione' : 'Start a conversation first');
      return;
    }
    
    setCreating(true);
    try {
      // Build full prompt from chat
      const fullPrompt = chatMessages
        .filter(m => m.role === 'user')
        .map(m => m.content)
        .join('\n\n');
      
      const response = await projectApi.create({
        prompt: fullPrompt,
        language,
        attachments,
        chat_history: chatMessages.map(m => ({ role: m.role, content: m.content })),
        analysis_mode: analysisMode
      });
      
      toast.success(language === 'it' ? 'Progetto creato' : 'Project created');
      handleDialogChange(false);
      navigate(`/projects/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* API Key Warning */}
      {!hasOpenRouterKey && (
        <Card className="bg-warning/10 border-warning/30">
          <CardContent className="py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Key className="w-5 h-5 text-warning" />
              <span className="text-warning font-medium">
                {language === 'it' 
                  ? 'Configura la tua API Key OpenRouter per iniziare'
                  : 'Configure your OpenRouter API Key to get started'}
              </span>
            </div>
            <Button 
              variant="outline" 
              className="border-warning text-warning hover:bg-warning/20"
              onClick={() => navigate('/settings')}
              data-testid="setup-api-btn"
            >
              {t('settings')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">{t('dashboard')}</h1>
          <p className="text-muted-foreground mt-1">
            {language === 'it' 
              ? 'Orchestrazione intelligente di esperti LLM' 
              : 'Intelligent LLM expert orchestration'}
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
          <DialogTrigger asChild>
            <Button 
              className="gap-2 bg-primary hover:bg-primary/90 shadow-[0_0_10px_rgba(59,130,246,0.3)]"
              disabled={!hasOpenRouterKey}
              data-testid="create-project-btn"
            >
              <Plus className="w-4 h-4" />
              {t('newProject')}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border sm:max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                {t('newProject')}
              </DialogTitle>
              <DialogDescription>
                {language === 'it' 
                  ? 'Chatta con il Manager per definire il tuo progetto. Puoi allegare file per fornire contesto.'
                  : 'Chat with the Manager to define your project. You can attach files for context.'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex-1 flex flex-col min-h-0 space-y-3 pt-2">
              {/* Warning: Analysis Only */}
              <div className="p-2 rounded-lg bg-info/10 border border-info/30">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-info mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-info">
                    {language === 'it' 
                      ? '📊 Questo strumento genera SOLO analisi e report, non codice funzionante.'
                      : '📊 This tool generates ONLY analysis and reports, not working code.'}
                  </p>
                </div>
              </div>
              
              {/* Analysis Mode Selection */}
              <div className="space-y-3">
                <Label>{language === 'it' ? 'Modalità di Analisi' : 'Analysis Mode'}</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div 
                    onClick={() => setAnalysisMode('specialists')}
                    className={`cursor-pointer rounded-lg border p-3 transition-all hover:bg-muted/50 ${
                      analysisMode === 'specialists' 
                        ? 'bg-primary/10 border-primary ring-1 ring-primary' 
                        : 'bg-card border-border'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                        analysisMode === 'specialists' ? 'border-primary' : 'border-muted-foreground'
                      }`}>
                        {analysisMode === 'specialists' && <div className="w-2 h-2 rounded-full bg-primary" />}
                      </div>
                      <span className="font-medium text-sm">
                        {language === 'it' ? 'Team di Esperti' : 'Specialized Team'}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground ml-6 leading-tight">
                      {language === 'it' 
                        ? 'Esperti con ruoli diversi per un\'analisi completa' 
                        : 'Experts with different roles for complete analysis'}
                    </p>
                  </div>

                  <div 
                    onClick={() => setAnalysisMode('comparison')}
                    className={`cursor-pointer rounded-lg border p-3 transition-all hover:bg-muted/50 ${
                      analysisMode === 'comparison' 
                        ? 'bg-primary/10 border-primary ring-1 ring-primary' 
                        : 'bg-card border-border'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                        analysisMode === 'comparison' ? 'border-primary' : 'border-muted-foreground'
                      }`}>
                        {analysisMode === 'comparison' && <div className="w-2 h-2 rounded-full bg-primary" />}
                      </div>
                      <span className="font-medium text-sm">
                        {language === 'it' ? 'Comparazione LLM' : 'LLM Comparison'}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground ml-6 leading-tight">
                      {language === 'it'
                        ? 'Confronta le risposte di diversi modelli AI'
                        : 'Compare responses from different AI models'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Analysis Types Checkboxes */}
              <div className="p-3 rounded-lg bg-muted/30 border border-border">
                <p className="text-xs font-medium mb-2">
                  {language === 'it' ? 'Tipi di analisi richieste (opzionale):' : 'Required analysis types (optional):'}
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(analysisTypeLabels).map(([key, labels]) => (
                    <label key={key} className="flex items-center gap-1.5 text-xs cursor-pointer hover:text-primary">
                      <input
                        type="checkbox"
                        checked={analysisTypes[key]}
                        onChange={(e) => setAnalysisTypes(prev => ({ ...prev, [key]: e.target.checked }))}
                        className="rounded border-border"
                      />
                      {labels[language] || labels.en}
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Chat Messages */}
              <div className="flex-1 min-h-[150px] max-h-[280px] overflow-y-auto pr-2 border rounded-lg bg-background/50">
                <div className="space-y-3 p-3">
                  {chatMessages.length === 0 && (
                    <div className="text-center py-6 text-muted-foreground text-sm">
                      {language === 'it' 
                        ? 'Descrivi cosa vuoi analizzare. Clicca "Crea Progetto" quando sei pronto.'
                        : 'Describe what you want to analyze. Click "Create Project" when ready.'}
                    </div>
                  )}
                  
                  {chatMessages.map((msg, idx) => (
                    <div 
                      key={idx} 
                      className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {msg.role === 'assistant' && (
                        <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                          <Bot className="w-4 h-4 text-primary" />
                        </div>
                      )}
                      <div className={`max-w-[80%] rounded-lg p-3 text-sm ${
                        msg.role === 'user' 
                          ? 'bg-primary text-primary-foreground' 
                          : 'bg-muted'
                      }`}>
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        {msg.ready && (
                          <div className="mt-2 flex items-center gap-1 text-xs text-success">
                            <CheckCircle2 className="w-3 h-3" />
                            {language === 'it' ? 'Pronto per creare il progetto!' : 'Ready to create project!'}
                          </div>
                        )}
                      </div>
                      {msg.role === 'user' && (
                        <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {analyzing && (
                    <div className="flex gap-2 justify-start">
                      <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <Loader2 className="w-4 h-4 text-primary animate-spin" />
                      </div>
                      <div className="bg-muted rounded-lg p-3 text-sm text-muted-foreground">
                        {language === 'it' ? 'Analizzando...' : 'Analyzing...'}
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
              </div>
              
              {/* Attachments */}
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {attachments.map((file, idx) => {
                    const FileIcon = getFileIcon(file.content_type);
                    return (
                      <div key={idx} className="flex items-center gap-1 bg-muted rounded-md px-2 py-1 text-xs">
                        <FileIcon className="w-3 h-3" />
                        <span className="max-w-[100px] truncate">{file.filename}</span>
                        <button onClick={() => removeAttachment(idx)} className="hover:text-destructive">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Input Area */}
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.txt,.csv,.docx,.png,.jpg,.jpeg,.webp,.md,.json,.xml"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={attachments.length >= MAX_FILES || analyzing}
                  title={language === 'it' ? 'Allega file' : 'Attach file'}
                >
                  <Paperclip className="w-4 h-4" />
                </Button>
                <Textarea
                  placeholder={language === 'it' ? 'Scrivi il tuo messaggio...' : 'Type your message...'}
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  className="flex-1 min-h-[60px] max-h-[100px] resize-none bg-background"
                  disabled={analyzing}
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={analyzing || (!currentMessage.trim() && attachments.length === 0)}
                  size="icon"
                  className="self-end"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Create Project Button - only show when analysis is ready */}
              {readyToCreate && (
                <Button
                  onClick={handleCreateProject}
                  disabled={creating || chatMessages.length === 0}
                  className="w-full gap-2"
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {language === 'it' ? 'Creazione...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      {language === 'it' ? 'Crea Progetto' : 'Create Project'}
                    </>
                  )}
                </Button>
              )}
              
              {/* Suggested Experts */}
              {suggestedExperts.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  <span className="font-medium">{language === 'it' ? 'Esperti suggeriti: ' : 'Suggested experts: '}</span>
                  {suggestedExperts.join(', ')}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border hover:border-primary/50 transition-colors">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                {t('totalProjects')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-heading font-bold">{stats.total_projects}</div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border hover:border-success/50 transition-colors">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                {t('completed')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-heading font-bold text-success">{stats.completed_projects}</div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border hover:border-warning/50 transition-colors">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                <DollarSign className="w-4 h-4" />
                {language === 'it' ? 'Costo Totale Consumato' : 'Total Cost Consumed'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-heading font-bold text-warning">
                {language === 'it' 
                  ? `€${(stats.total_cost * 0.92)?.toFixed(2)}`
                  : `$${stats.total_cost?.toFixed(2)}`}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border hover:border-primary/50 transition-colors">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                <Hash className="w-4 h-4" />
                {language === 'it' ? 'Totale Token Consumati' : 'Total Tokens Consumed'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <div className="text-3xl font-heading font-bold">{(stats.total_tokens / 1000).toFixed(0)}k</div>
                <TrendingUp className="w-5 h-5 text-primary" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Templates Section - Always visible */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-heading font-semibold flex items-center gap-2">
              <Bookmark className="w-5 h-5 text-primary" />
              {language === 'it' ? 'I Miei Template' : 'My Templates'}
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              {language === 'it' 
                ? 'Configurazioni salvate per riutilizzarle in nuovi progetti.'
                : 'Saved configurations to reuse in new projects.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {templates.length > 0 && (
              <div className="relative w-48">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={language === 'it' ? 'Cerca template...' : 'Search templates...'}
                  value={templateSearch}
                  onChange={(e) => setTemplateSearch(e.target.value)}
                  className="pl-9 h-8 text-sm"
                />
              </div>
            )}
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowCreateTemplateDialog(true)}
              className="gap-1"
            >
              <Plus className="w-4 h-4" />
              {language === 'it' ? 'Nuovo template progetto' : 'New Project Template'}
            </Button>
            {templates.length > 3 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowTemplates(!showTemplates)}
              >
                {showTemplates 
                  ? (language === 'it' ? 'Nascondi' : 'Hide')
                  : (language === 'it' ? 'Mostra tutti' : 'Show all')}
              </Button>
            )}
          </div>
        </div>
        
        {templates.length === 0 ? (
          <Card className="bg-card/50 border-dashed border-border">
            <CardContent className="flex flex-col items-center justify-center py-8">
              <Bookmark className="w-10 h-10 text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground text-center text-sm">
                {language === 'it' 
                  ? 'Nessun template salvato. Crea un template per salvare configurazioni di esperti riutilizzabili.'
                  : 'No templates saved. Create a template to save reusable expert configurations.'}
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-4 gap-1"
                onClick={() => setShowCreateTemplateDialog(true)}
              >
                <Plus className="w-4 h-4" />
                {language === 'it' ? 'Crea il tuo primo template progetto' : 'Create your first project template'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {(showTemplates ? templates : templates.slice(0, 3))
              .filter(template => {
                if (!templateSearch.trim()) return true;
                const searchLower = templateSearch.toLowerCase();
                return (template.name || '').toLowerCase().includes(searchLower) ||
                       (template.description || '').toLowerCase().includes(searchLower);
              })
              .map(template => (
              <Card 
                key={template.id} 
                className="bg-card border-border hover:border-primary/50 transition-all cursor-pointer group"
                onClick={() => {
                  setDialogOpen(true);
                  const templateInfo = language === 'it'
                    ? `Voglio creare un progetto usando il template "${template.name}" con ${template.experts?.length || 0} esperti pre-configurati (${template.accuracy_level}).`
                    : `I want to create a project using the template "${template.name}" with ${template.experts?.length || 0} pre-configured experts (${template.accuracy_level}).`;
                  setCurrentMessage(templateInfo);
                }}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{template.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {template.experts?.length || 0} {language === 'it' ? 'esperti' : 'experts'} • {template.accuracy_level}
                      </p>
                      {template.description && (
                        <p className="text-xs text-muted-foreground/70 truncate mt-1">{template.description}</p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => handleDeleteTemplate(template.id, e)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Template Dialog */}
      <Dialog open={showCreateTemplateDialog} onOpenChange={setShowCreateTemplateDialog}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bookmark className="w-5 h-5 text-primary" />
              {language === 'it' ? 'Nuovo Template Progetto' : 'New Project Template'}
            </DialogTitle>
            <DialogDescription>
              {language === 'it' 
                ? 'Salva una configurazione di esperti da riutilizzare in futuri progetti.'
                : 'Save an expert configuration to reuse in future projects.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{language === 'it' ? 'Nome Template' : 'Template Name'} *</Label>
              <Input
                value={newTemplate.name}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
                placeholder={language === 'it' ? 'Es: Analisi di Mercato' : 'E.g.: Market Analysis'}
                className="bg-background"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{language === 'it' ? 'Descrizione' : 'Description'}</Label>
              <Input
                value={newTemplate.description}
                onChange={(e) => setNewTemplate(prev => ({ ...prev, description: e.target.value }))}
                placeholder={language === 'it' ? 'Descrizione opzionale' : 'Optional description'}
                className="bg-background"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'it' ? 'Dimensione Team' : 'Team Size'}</Label>
                <select
                  value={newTemplate.team_size}
                  onChange={(e) => setNewTemplate(prev => ({ ...prev, team_size: e.target.value }))}
                  className="w-full h-10 px-3 rounded-md bg-background border border-input text-sm"
                >
                  <option value="minimal">{language === 'it' ? 'Semplificato' : 'Minimal'}</option>
                  <option value="normal">{language === 'it' ? 'Normale' : 'Normal'}</option>
                  <option value="extended">{language === 'it' ? 'Esteso' : 'Extended'}</option>
                  <option value="custom">{language === 'it' ? 'Personalizzato' : 'Custom'}</option>
                </select>
              </div>
              
              <div className="space-y-2">
                <Label>{language === 'it' ? 'Livello Accuratezza' : 'Accuracy Level'}</Label>
                <select
                  value={newTemplate.accuracy_level}
                  onChange={(e) => setNewTemplate(prev => ({ ...prev, accuracy_level: e.target.value }))}
                  className="w-full h-10 px-3 rounded-md bg-background border border-input text-sm"
                >
                  <option value="economic">💰 {language === 'it' ? 'Economico' : 'Economic'}</option>
                  <option value="base">📊 {language === 'it' ? 'Base' : 'Basic'}</option>
                  <option value="intermediate">⚖️ {language === 'it' ? 'Intermedio' : 'Intermediate'}</option>
                  <option value="advanced">🚀 {language === 'it' ? 'Avanzato' : 'Advanced'}</option>
                  <option value="maximum">🧠 {language === 'it' ? 'Massimo' : 'Maximum'}</option>
                </select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{language === 'it' ? 'Esperti Pre-configurati' : 'Pre-configured Experts'}</Label>
              <div className="flex gap-2">
                <Input
                  value={newExpert.role}
                  onChange={(e) => setNewExpert(prev => ({ ...prev, role: e.target.value }))}
                  placeholder={language === 'it' ? 'Nome esperto (es: Analista)' : 'Expert name (e.g.: Analyst)'}
                  className="bg-background flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && addExpertToTemplate()}
                />
                <Button variant="outline" size="icon" onClick={addExpertToTemplate}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {newTemplate.experts.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {newTemplate.experts.map((expert, index) => (
                    <Badge key={index} variant="secondary" className="gap-1 pr-1">
                      {expert.role}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-4 w-4 hover:bg-destructive/20"
                        onClick={() => removeExpertFromTemplate(index)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {language === 'it' 
                  ? 'Aggiungi esperti che vuoi includere in questo template. Premi Enter o + per aggiungere.'
                  : 'Add experts you want to include in this template. Press Enter or + to add.'}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTemplateDialog(false)}>
              {language === 'it' ? 'Annulla' : 'Cancel'}
            </Button>
            <Button onClick={handleCreateTemplate} disabled={!newTemplate.name.trim()}>
              {language === 'it' ? 'Salva Template' : 'Save Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Projects List */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-heading font-semibold">{t('recentProjects')}</h2>
          {projects.length > 0 && (
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={language === 'it' ? 'Cerca progetti...' : 'Search projects...'}
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
                className="pl-9 h-9"
              />
              {projectSearch && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6"
                  onClick={() => setProjectSearch('')}
                >
                  <X className="w-3 h-3" />
                </Button>
              )}
            </div>
          )}
        </div>
        {projects.length === 0 ? (
          <Card className="bg-card border-border border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <MessageSquare className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-center">
                {t('noProjects')}
              </p>
              <Button 
                className="mt-4 gap-2" 
                onClick={() => setDialogOpen(true)}
                disabled={!hasOpenRouterKey}
                data-testid="empty-create-btn"
              >
                <Plus className="w-4 h-4" />
                {t('newProject')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {projects
              .filter(project => {
                if (!projectSearch.trim()) return true;
                const searchLower = projectSearch.toLowerCase();
                const name = (project.name || '').toLowerCase();
                const prompt = (project.prompt || '').toLowerCase();
                return name.includes(searchLower) || prompt.includes(searchLower);
              })
              .map((project) => (
              <Card 
                key={project.id}
                className="bg-card border-border hover:border-primary/50 transition-all cursor-pointer group"
                onClick={() => navigate(`/projects/${project.id}`)}
                data-testid={`project-card-${project.id}`}
                data-testid={`project-card-${project.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-foreground font-medium truncate group-hover:text-primary transition-colors">
                        {project.name || project.prompt.substring(0, 100)}
                        {!project.name && project.prompt.length > 100 && '...'}
                      </p>
                      
                      <div className="flex flex-wrap gap-2 mt-2">
                         {/* Team Size Badge */}
                         {project.team_size && (
                          <Badge variant="outline" className="text-xs font-normal gap-1 px-1.5 h-5 bg-muted/50 text-muted-foreground border-border">
                            <Users className="w-3 h-3" />
                            {project.team_size === 'minimal' ? (language === 'it' ? 'Minimale' : 'Minimal') :
                             project.team_size === 'normal' ? (language === 'it' ? 'Normale' : 'Normal') :
                             project.team_size === 'extended' ? (language === 'it' ? 'Esteso' : 'Extended') :
                             project.team_size === 'custom' ? (language === 'it' ? 'Custom' : 'Custom') :
                             project.team_size}
                          </Badge>
                        )}
                        
                        {/* Accuracy Badge */}
                        {project.accuracy_level && (
                          <Badge variant="outline" className="text-xs font-normal gap-1 px-1.5 h-5 bg-muted/50 text-muted-foreground border-border">
                            <Target className="w-3 h-3" />
                            {project.accuracy_level === 'economic' ? '💰' : 
                             project.accuracy_level === 'base' ? '📊' : 
                             project.accuracy_level === 'intermediate' ? '⚖️' : 
                             project.accuracy_level === 'advanced' ? '🚀' : 
                             project.accuracy_level === 'maximum' ? '🧠' : ''}
                            {project.accuracy_level.charAt(0).toUpperCase() + project.accuracy_level.slice(1)}
                          </Badge>
                        )}

                        {/* Analysis Mode Badge */}
                        {project.analysis_mode && (
                          <Badge variant="outline" className="text-xs font-normal gap-1 px-1.5 h-5 bg-muted/50 text-muted-foreground border-border">
                            <LayoutList className="w-3 h-3" />
                            {['comparison', 'compare'].includes(project.analysis_mode)
                              ? (language === 'it' ? 'Comparazione' : 'Comparison')
                              : (language === 'it' ? 'Specialisti' : 'Specialists')}
                          </Badge>
                        )}
                      </div>

                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="font-code">
                          {new Date(project.created_at).toLocaleString(language === 'it' ? 'it-IT' : 'en-US')}
                        </span>
                        {project.total_cost > 0 && (
                          <span className="flex items-center gap-1" title={language === 'it' ? 'Costo del progetto' : 'Project cost'}>
                            {language === 'it' ? '€' : '$'}
                            {language === 'it' 
                              ? (project.total_cost * 0.92).toFixed(4)
                              : project.total_cost.toFixed(4)}
                          </span>
                        )}
                      </div>
                    </div>
                    <StatusBadge status={project.status} phase={project.phase} language={language} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
